import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Shield, CheckCircle, BookOpen, Scale } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { authApi } from '@/lib/api'

export default function Login() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const loginError = searchParams.get('error')

  // Only configured identity providers get a button (Google is the default
  // even if the probe fails, so the page never becomes a dead end).
  const { data: providers } = useQuery({
    queryKey: ['auth-providers'],
    queryFn: authApi.getProviders,
    staleTime: Infinity,
  })

  const handleLogin = (provider: 'google' | 'microsoft') => {
    // Pricing-page choice (e.g. /login?plan=FREE&next=/) carried into OAuth.
    const plan = searchParams.get('plan') || undefined
    const next = searchParams.get('next') || undefined
    window.location.href = authApi.getLoginUrl(provider, plan, next)
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-4">
      {/* Atmospheric backdrop: institutional dotted grid + floating brand glows */}
      <div className="absolute inset-0 bg-dots text-mim-blue/15 dark:text-blue-300/10" aria-hidden="true" />
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-mim-blue/15 dark:bg-blue-500/10 blur-3xl animate-float-slow"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-40 -right-24 w-[28rem] h-[28rem] rounded-full bg-mim-orange/15 dark:bg-mim-orange/10 blur-3xl animate-float-slow"
        style={{ animationDelay: '-4.5s' }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md stagger">
        {/* Logo card */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl shadow-mim-blue/10 overflow-hidden border border-mim-blue/10">
          {/* Barra istituzionale slim (Design system .italia) */}
          <div className="it-slim-bar px-8 py-2">
            <p className="text-[11px] font-semibold tracking-wide text-white">
              Sistema di governance scolastica per la conformità IA
            </p>
          </div>
          {/* Header */}
          <div className="relative bg-gradient-to-br from-mim-blue to-blue-950 px-8 pt-7 pb-6 overflow-hidden">
            <div className="absolute inset-0 bg-dots text-white/10" aria-hidden="true" />
            <div className="relative flex items-center gap-3 mb-4">
              <img src="/logo.png" alt="ValutazIA" className="h-14 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-white leading-none">ValutazIA</h1>
                <p className="text-blue-200 text-sm">Valutazione del Rischio IA a scuola</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-6">
            {loginError === 'workspace_required' && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-rosso-light border border-rosso/30 text-rosso-dark text-sm">
                <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Accesso riservato agli account <strong>istituzionali della scuola</strong>{' '}
                  (Google Workspace o Microsoft 365). Gli account personali{' '}
                  (<strong>@gmail.com</strong>, <strong>@outlook.com</strong>, …) non sono ammessi.
                  Se sei il <strong>DPO</strong> dell'istituto, accedi con l'indirizzo email
                  comunicato dalla scuola alla piattaforma.
                </span>
              </div>
            )}
            {loginError === 'oauth_failed' && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-rosso-light border border-rosso/30 text-rosso-dark text-sm">
                <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Accesso non riuscito. Riprova o contatta l'assistenza.</span>
              </div>
            )}
            <h2 className="text-xl font-semibold text-foreground mb-1 text-center">
              {t('auth.loginTitle')}
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              {t('auth.loginDesc')}
            </p>

            {/* Feature list */}
            <div className="space-y-3 mb-6">
              {[
                { icon: Scale, text: 'Conformità AI Act Reg. (UE) 2024/1689' },
                { icon: BookOpen, text: 'Linee Guida MIM DM 166/2025' },
                { icon: Shield, text: 'Tutela minori e supervisione umana' },
                { icon: CheckCircle, text: 'Report PDF con citazioni normative' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Icon className="w-4 h-4 text-mim-blue flex-shrink-0" />
                  {text}
                </div>
              ))}
            </div>

            {/* Login buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => handleLogin('google')}
                className="w-full bg-mim-blue hover:bg-blue-800 text-white h-11"
                size="lg"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t('auth.loginGoogle')}
              </Button>

              {providers?.microsoft && (
                <Button
                  onClick={() => handleLogin('microsoft')}
                  variant="outline"
                  className="w-full h-11 border-mim-blue/40 text-mim-blue hover:bg-mim-lightblue dark:text-blue-300 dark:hover:bg-blue-950"
                  size="lg"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
                    <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
                    <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
                    <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
                  </svg>
                  {t('auth.loginMicrosoft')}
                </Button>
              )}
            </div>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Usa il tuo account <strong>istituzionale</strong> della scuola
              (Google Workspace o Microsoft 365)
            </p>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-muted/50 border-t">
            <p className="text-xs text-center text-muted-foreground">
              Sistema ad uso interno scolastico · ValutazIA
            </p>
          </div>
        </div>

        {/* Legal note */}
        <p className="text-center text-slate-500 dark:text-slate-400 text-xs mt-6 px-4">
          Riferimenti: Reg. (UE) 2024/1689 – AI Act · Linee Guida MIM DM 166/2025 · GDPR 2016/679
        </p>
      </div>
    </div>
  )
}
